// src/components/GuestGroups.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { db, auth } from "../firebase";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const MAX_IN_QUERY = 10;

const chunkIds = (ids) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += MAX_IN_QUERY) {
    chunks.push(ids.slice(i, i + MAX_IN_QUERY));
  }
  return chunks;
};

const GuestGroups = () => {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupName, setGroupName] = useState("");
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlShareGroupId = params.get("id");
  const [shareGroupId, setShareGroupId] = useState(urlShareGroupId);

  useEffect(() => {
    if (urlShareGroupId) {
      localStorage.setItem("guestShareGroupId", urlShareGroupId);
      setShareGroupId(urlShareGroupId);
      return;
    }
    const cached = localStorage.getItem("guestShareGroupId");
    if (cached) {
      setShareGroupId(cached);
    }
  }, [urlShareGroupId]);

  useEffect(() => {
    if (user || !shareGroupId) return;
    signInAnonymously(auth).catch((error) => {
      console.error("Anonymous sign-in error:", error);
    });
  }, [shareGroupId, user]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const fetchGroupName = async () => {
      const groupDocRef = doc(db, "sharegroups", shareGroupId);
      const groupSnap = await getDoc(groupDocRef);
      if (groupSnap.exists()) {
        setGroupName(groupSnap.data().name);
      } else {
        setGroupName("");
      }
    };
    fetchGroupName();
  }, [shareGroupId]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      setRuns(Array.from(runsMap.values()));
    };

    const subscribeSharedRuns = (collectionName, filterField, filterValue) => {
      if (!filterValue) return;
      const runsRef = collection(db, collectionName);
      const q = query(runsRef, where(filterField, "==", filterValue));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docs.forEach((docSnap) => {
          runsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
        upsertRuns();
      });
      unsubscribers.push(unsubscribe);
    };

    subscribeSharedRuns("events", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("runs", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("events", "sharedGroupName", groupName);
    subscribeSharedRuns("runs", "sharedGroupName", groupName);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [groupName, shareGroupId]);

  useEffect(() => {
    const fetchGroups = async () => {
      if (!shareGroupId || runs.length === 0) {
        setGroups([]);
        return;
      }

      const racerIds = new Set();
      runs.forEach((run) => {
        if (!Array.isArray(run.racers)) return;
        run.racers.forEach((entry) => {
          if (entry?.racerId) {
            racerIds.add(entry.racerId);
          }
        });
      });

      const uniqueIds = Array.from(racerIds);
      if (uniqueIds.length === 0) {
        setGroups([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const chunks = chunkIds(uniqueIds);
      const groupSet = new Set();

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (Array.isArray(data.groups)) {
            data.groups.forEach((group) => {
              if (group && group.trim()) {
                groupSet.add(group.trim());
              }
            });
          }
        });
      }

      const groupsList = Array.from(groupSet).sort((a, b) => a.localeCompare(b));
      setGroups(groupsList);
    };

    fetchGroups();
  }, [runs, shareGroupId]);

  if (!shareGroupId) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Missing share link.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2>Groups in Public Runs</h2>
      {groups.length === 0 ? (
        <p>No groups available.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0 }}>
          {groups.map((group) => (
            <li
              key={group}
              style={{
                marginBottom: "10px",
                borderBottom: "1px solid #ccc",
                padding: "10px 0",
              }}
            >
              <strong>{group}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GuestGroups;
